// Adapted from: https://github.com/bmcmahen/react-page-controller which is distributed under MIT license
// Copying over code and rewriting some of it due to original author's lack of maintenance of dependencies, bugs / organizational issues
import * as React from 'react'

import {BreakStyle, defineStylesWithBreaks, useThemedStylesWithBreaks} from './styles/style'
import {Callbacks, ResponderEvent, StateType, useGestureResponder} from './gestureResponder'
import {SpringConfig, animated, useSpring} from '@react-spring/web'

import {ActionButton} from './ActionButton'
import {SVG} from '.'
import {useDetectInputDevices, useResizeObserver} from './hooks'
import useScrollLock from './hooks/useScrollLock'

const pageDotBorderWidthNum = 1
const pageDotSizeNum = 4
const pageDotSize = `${pageDotSizeNum}px`
const pageDotRadius = `${pageDotSizeNum * 2}px`

const style = (navigationOnTop: boolean) =>
  defineStylesWithBreaks((theme) => {
    const pageDot: BreakStyle = {
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.accent.base,
      borderRadius: pageDotRadius,
      height: pageDotRadius,
      width: pageDotRadius,
      marginTop: pageDotRadius,
      marginBottom: pageDotRadius
    }

    const pageDotButton: BreakStyle = {
      paddingLeft: pageDotSize,
      paddingRight: pageDotSize,
      cursor: 'pointer'
    }

    return {
      container: {
        overflow: 'hidden',
        width: '100%',
        position: 'relative',
        display: 'flex',

        mobile: {
          flex: 'initial'
        },
        md: {
          flex: 'initial'
        },
        lg: {
          flex: 3
        }
      },
      pageControl: {
        position: 'absolute',
        bottom: '5%',
        marginLeft: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        backgroundColor: navigationOnTop ? theme.core.b1 : 'transparent',
        borderRadius: `${pageDotSizeNum * 6 + pageDotBorderWidthNum * 2}px`
      },
      pageDot,
      pageDotFilled: {
        ...pageDot,
        backgroundColor: theme.accent.base
      },
      pageDotButton,
      pageDotButtonFirst: {
        ...pageDotButton,
        paddingLeft: pageDotRadius
      },
      pageDotButtonLast: {
        ...pageDotButton,
        paddingRight: pageDotRadius
      },
      previewImageStyle: {
        height: '100%',
        width: 'auto'
      },
      arrowLeft: {
        position: 'absolute',
        marginTop: '0',
        transform: 'translateY(-50%) rotate(90deg)',
        top: '50%',
        left: '8px',
        alignItems: 'center'
      },
      arrowRight: {
        position: 'absolute',
        marginTop: '0',
        transform: 'translateY(-50%) rotate(-90deg)',
        top: '50%',
        right: '8px',
        alignItems: 'center'
      }
    }
  })

/**
 * ReactPager
 *
 * Provide views that can be swiped left or right (with touch devices).
 */

export interface PagerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: Array<React.ReactNode>
  navigationOnTop?: boolean
  enableMouse?: boolean
  enableGestures?: boolean
  focusOnChange?: boolean
  enableScrollLock?: boolean
  animationConfig?: SpringConfig
  lazyLoad?: boolean
  onSetLazy?: (suggestedIndex: number) => number[]
  onTerminationRequest?: Callbacks['onTerminationRequest']
  /** Optionally override onMoveShouldSet defaults */
  onMoveShouldSet?: (state: StateType, e: ResponderEvent, suggested: boolean) => boolean
}

export interface PagerHandles {
  focus(i?: number): void
}

export interface CallbackProps {
  style: React.CSSProperties
  'aria-hidden': boolean
  ref: (el: HTMLDivElement | null) => void
}

export type PagerChildCallback = (props: CallbackProps, active: boolean, load: boolean) => React.ReactNode

const PageControllerInternal: React.ForwardRefRenderFunction<PagerHandles, PagerProps> = (
  {
    children,
    id,
    navigationOnTop = true,
    focusOnChange = false,
    enableScrollLock = true,
    enableGestures = true,
    enableMouse = false,
    lazyLoad = false,
    onSetLazy,
    animationConfig = {tension: 190, friction: 20, mass: 0.4},
    onTerminationRequest,
    onMoveShouldSet,
    ...other
  },
  ref
) => {
  const [index, setIndex] = React.useState(0)
  const {hasMouse} = useDetectInputDevices()
  const s = useThemedStylesWithBreaks(style(navigationOnTop))
  const [isDragging, setIsDragging] = React.useState(false)
  const [loaded, setLoaded] = React.useState(() => new Set(onSetLazy ? onSetLazy(index) : [index]))
  const {ref: setContainerRef, width = 0} = useResizeObserver<HTMLDivElement>()
  const childrenRefs = React.useRef<Map<number, HTMLDivElement | null>>(new Map())

  const onRequestChange = (i: number) => setIndex(i)

  const indexRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    indexRef.current = index
  }, [index])
  const previousIndex = indexRef.current
  const shouldFocusRef = React.useRef<number | null>(null)

  useScrollLock(isDragging && enableScrollLock)

  React.useEffect(() => {
    if (typeof previousIndex === 'number' && previousIndex !== index) {
      shouldFocusRef.current = index
    } else {
      shouldFocusRef.current = null
    }
  }, [previousIndex, index])

  const focusByIndex = React.useCallback(
    (i: number) => {
      const el = childrenRefs.current.get(i)
      if (el) {
        el.focus({preventScroll: true})
      }
    },
    [childrenRefs]
  )

  // expose an imperative focus function which focuses
  // the currently active index
  React.useImperativeHandle(ref, () => ({
    focus: (i?: number) => {
      focusByIndex(i || index)
    }
  }))

  const [{x}, set] = useSpring(() => ({
    x: index * -100,
    config: animationConfig
  }))

  /**
   * Potentially autofocus after our animation
   */

  const onRest = React.useCallback(() => {
    if (typeof shouldFocusRef.current === 'number') {
      focusByIndex(shouldFocusRef.current)
    }
  }, [focusByIndex])

  const renderableChildren = children.filter((child) => child !== null)

  // gesture view counts
  const childCount = renderableChildren.length
  const maxIndex = childCount - 1
  const minIndex = 0

  /**
   * Prevent invalid indexes
   */

  function isValidNextIndex(index: number) {
    return index > 0 && index <= maxIndex
  }

  /**
   * We keep a set of indexes that should
   * be loaded for lazy loading.
   */

  function addIndexToLoaded(index: number) {
    if (!isValidNextIndex(index)) {
      return
    }

    let indexes: number | number[] = index

    // allow the user to customize which indexes to load
    if (onSetLazy) {
      indexes = onSetLazy(index)
    }

    const indexesArray = Array.isArray(indexes) ? indexes : [indexes]
    const next = new Set(loaded)

    indexesArray.forEach((i) => {
      // don't set items which are already loaded or are invalid
      if (loaded.has(i) || !isValidNextIndex(index)) {
        return
      }

      next.add(i)
    })

    setLoaded(next)
  }

  // animate into position if our index changes
  React.useEffect(() => {
    set({
      x: index * -100,
      onRest
    })
    loaded.add(index)
  }, [index, loaded, onRest, set])

  /**
   * Handle gesture end event (either touchend
   * or pan responder termination).
   */

  function releaseToPosition(x: number) {
    // if it's over 50% in either direction, move to that index.
    // otherwise, snap back to existing index.
    const threshold = width / 2
    if (Math.abs(x) > threshold) {
      if (x < 0 && index < maxIndex) {
        onRequestChange(index + 1)
      } else if (x > 0 && index > minIndex) {
        onRequestChange(index - 1)
      } else {
        set({x: index * -100})
      }
    } else {
      // return back!
      set({x: index * -100, onRest})
    }
  }

  function onTermination({delta}: StateType) {
    setIsDragging(false)
    releaseToPosition(delta[0])
  }

  function onEnd({delta, velocity, direction}: StateType) {
    const [x] = delta
    setIsDragging(false)

    // 1. If the force is great enough, switch to the previous index
    if (velocity > 0.2 && direction[0] > 0 && index > minIndex) {
      return onRequestChange(index - 1)
    }

    // or the next index, depending on direction
    if (velocity > 0.2 && direction[0] < 0 && index < maxIndex) {
      return onRequestChange(index + 1)
    }

    releaseToPosition(x)
  }

  /**
   * Observe our pan-responder to enable gestures
   */

  const {bind} = useGestureResponder(
    {
      onTerminationRequest,
      onStartShouldSet: () => {
        if (!enableGestures) {
          return false
        }

        return false
      },
      onMoveShouldSet: (state, e) => {
        const {initialDirection} = state
        if (!enableGestures) {
          return false
        }

        const set = initialDirection[0] !== 0

        // allow the user to tap into this component to potentially
        // override it
        if (onMoveShouldSet) {
          return onMoveShouldSet(state, e, set)
        }

        return set
      },
      onGrant: () => {
        setIsDragging(true)
      },
      onMove: ({delta, direction}) => {
        const [x] = delta
        const xPos = (x / width) * 100 + index * -100

        set({
          x: xPos,
          immediate: true,
          onRest: () => {}
        })

        // lazy load the item we are swiping towards
        addIndexToLoaded(direction[0] > 0 ? index - 1 : index + 1)
      },
      onRelease: onEnd,
      onTerminate: onTermination
    },
    {
      uid: id,
      enableMouse
    }
  )

  const content = (
    <div {...bind} ref={setContainerRef} className="Gesture-view" style={{width: '100%'}} {...other}>
      <animated.div
        className="Gesture-view__animated-container"
        style={{
          flexDirection: 'row',
          direction: 'ltr',
          willChange: 'transform',
          minHeight: 0,
          flex: 1,
          display: 'flex',
          transform: x.to((x) => `translateX(${taper(x, maxIndex * -100)}%)`)
        }}
      >
        {renderableChildren.map((child, i) => {
          const styles: React.CSSProperties = {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            flexShrink: 0,
            height: '100%',
            overflow: 'hidden',
            outline: 'none'
          }

          const props = {
            tabIndex: index === i ? 0 : -1,
            style: styles,
            'aria-hidden': i !== index,
            ref: (el: HTMLDivElement | null) => {
              childrenRefs.current!.set(i, el)
            }
          }

          const load = !lazyLoad || index === i || loaded.has(i)

          if (typeof child === 'function') {
            return React.cloneElement(child, props, index === i, load)
          }

          return (
            <div key={i} className="Gesture-view__pane" {...props}>
              {load && child}
            </div>
          )
        })}
      </animated.div>
    </div>
  )

  const pageControl =
    renderableChildren.length > 1 ? (
      <div style={s.pageControl}>
        {renderableChildren.map((_, i) => {
          return (
            <div
              key={i}
              style={
                i === 0
                  ? s.pageDotButtonFirst
                  : i === renderableChildren.length - 1
                    ? s.pageDotButtonLast
                    : s.pageDotButton
              }
              onMouseDown={() => setIndex(i)}
            >
              <div style={index === i ? s.pageDotFilled : s.pageDot} />
            </div>
          )
        })}
      </div>
    ) : null

  const pageArrows =
    renderableChildren.length > 1 && hasMouse ? (
      <>
        {index !== 0 ? (
          <ActionButton
            orientation="vertical"
            appearance="plain"
            size="small"
            style={s.arrowLeft}
            iconCreator={(color) => <SVG.ChevronSmall color={color} />}
            onClick={() => setIndex(index - 1)}
          />
        ) : null}
        {index !== renderableChildren.length - 1 ? (
          <ActionButton
            appearance="plain"
            size="small"
            orientation="vertical"
            style={s.arrowRight}
            iconCreator={(color) => <SVG.ChevronSmall color={color} />}
            onClick={() => setIndex(index + 1)}
          />
        ) : null}
      </>
    ) : null

  return (
    <div style={s.container}>
      {content}
      {pageControl}
      {pageArrows}
    </div>
  )
}

export const PageController = React.forwardRef(PageControllerInternal)

/**
 * Add some resistance when swiping in a direction
 * that doesn't contain another pane
 */

function taper(x: number, maxWidth: number) {
  if (x > 0) {
    return x * 0.3
  }

  if (x < maxWidth) {
    const diff = x - maxWidth
    return x - diff * 0.7
  }

  return x
}
